/* eslint-disable react/prop-types */
import { useMemo } from 'react';
import { Link as MuiLink, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import _ from 'lodash';

import { PageLink } from '../../links/Links';
import Link, { buildLinkUrl } from '../../links/Link';
import { DrillInLink } from '../../links/DrillInLink';
import { useFilterState } from '../../../stores/FilterStateStore';
import { usePageState } from '../../../stores/PageStateStore';
import format from '../../../utilities/FormattingUtilities';
import { useRole } from '../../../stores/SessionStore';
import { INNER_CELL_VALUE_CLASS } from '../DataTableConstants';

// TODO - dependency issue. We'll probably need to move filter IDs into their own constants file or something that does not have a dependency on something that uses the datatable
// note, there's a circular dependency issue here. Link -> TestFilter -> EntityListFilter. The EntityListFilter require DataTable. Datatable requires DataTableColumns -> Link.
// import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
const testFilterId = 'Test';

const useLinkColumnStyles = makeStyles((theme) => ({
    // Style applied when user does not have permission to drill into a table entity and we're just displaying text
    noLinkText: {
        fontWeight: 500,
    },
    clickHandlerLink: {
        color: theme.palette.type === 'light' ? theme.palette.primary.main : theme.palette.text.primary,
        fontWeight: 500,
    },
}));

export default function LinkColumn(props) {
    const styles = useLinkColumnStyles();
    const { column, row } = props;
    const { link, formatter, mapper } = column;
    const hasClickHandler = _.isFunction(link?.clickHandler);
    const [filterState] = useFilterState();
    const [pageState] = usePageState();
    const targetUrl = useMemo(
        () => (!hasClickHandler ? buildLinkUrl(link, row, filterState, pageState) : null),
        [hasClickHandler, link, row, filterState, pageState]
    );

    const [userRole] = useRole();

    let value = mapper(row, column);

    if (formatter) {
        if (_.isFunction(formatter)) {
            value = formatter(value);
        } else {
            value = format(value, _.isString(formatter) ? { type: formatter } : formatter);
        }
    }

    if (link?.sysAdminOnly && userRole.toLowerCase() !== 'super') {
        return <div className={`${clsx(INNER_CELL_VALUE_CLASS, column.className, styles.noLinkText)}`}>{value}</div>;
    }

    if (hasClickHandler) {
        return (
            <MuiLink
                className={styles.clickHandlerLink}
                component="button"
                variant="body2"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    link.clickHandler(row, column, event);
                }}
            >
                {value}
            </MuiLink>
        );
    }

    if (!targetUrl) {
        return <div className={`${clsx(INNER_CELL_VALUE_CLASS, column.className, styles.noLinkText)}`}>{value}</div>;
    }

    return (
        <props.linkComponent to={targetUrl} plmIdDefault={row?.plmId} state={link.state}>
            {value}
        </props.linkComponent>
    );
}

export function drillInLinkColumnRenderer(row, column) {
    return <LinkColumn linkComponent={DrillInLink} column={column} row={row} />;
}

export function linkColumnRenderer(row, column) {
    return <LinkColumn linkComponent={Link} column={column} row={row} />;
}

const useIconLinkStyles = makeStyles(() => ({
    root: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        '&, & .MuiSvgIcon-root, & .MuiIcon-root': {
            fontSize: '18px',
            height: '18px',
            lineHeight: '18px',
        },
    },
}));

function IconLinkColumn(props) {
    const styles = useLinkColumnStyles();
    const iconStyles = useIconLinkStyles();
    const { column, row } = props;
    const { link, formatter, mapper } = column;
    const [filterState] = useFilterState();
    const [pageState] = usePageState();
    const targetUrl = useMemo(
        () => buildLinkUrl(link, row, filterState, pageState),
        [link, row, filterState, pageState]
    );

    let value = mapper(row, column);

    if (formatter && _.isFunction(formatter)) {
        value = formatter(value, row);
    }

    const iconContent = (
        <span className={iconStyles.root}>
            <span className={iconStyles.icon}>{value}</span>
        </span>
    );

    if (!targetUrl) {
        return (
            <div className={`${clsx(INNER_CELL_VALUE_CLASS, column.className, styles.noLinkText)}`}>{iconContent}</div>
        );
    }

    return (
        <Link to={targetUrl} plmIdDefault={row?.plmId} state={link?.state}>
            {iconContent}
        </Link>
    );
}

export function iconLinkColumnRenderer(row, column) {
    return <IconLinkColumn column={column} row={row} />;
}

export function TestLogLinkColumn(props) {
    const { column, row } = props;
    const { link } = column;
    const test = column?.link?.filterState?.current?.[testFilterId];
    const maybeTest = row?.[column.mapping];
    const isPathEnabled = column?.isPathEnabled || test?.traceRoute?.enabled || maybeTest?.traceRoute?.enabled;
    const [filterState] = useFilterState();
    const [pageState] = usePageState();
    const targetUrl = useMemo(
        () => buildLinkUrl(link, row, filterState, pageState),
        [link, row, filterState, pageState]
    );

    return (
        <Link to={targetUrl} plmIdDefault={row?.plmId}>
            {isPathEnabled ? 'Test/Path Log' : 'Test Log'}
        </Link>
    );
}

export function testLogLinkColumnRenderer(row, column) {
    return <TestLogLinkColumn column={column} row={row} />;
}

export function problemLinkColumnRenderer(row, column) {
    const entity = row?.[column.mapping];
    const filters = [
        {
            name: entity.type,
            uuid: entity.uuid,
        },
    ];
    return <PageLink target={`/${entity.page}`} targetName={entity.name} filters={filters} />;
}
